import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';

import moment from 'moment';
import { keyframes } from '@emotion/react';
import {
  getCurriculumsForHomePage,
  getCurriculumState,
  ICurriculumForHome,
} from '../../store/CurriculumSlice';
import { getBookmark, getBookmarkState } from '../../store/BookmarkSlice';
import { getMetaTagState } from '../../store/MetaTagSlice';
import { getUsageByUserId } from '../../store/AccessLimitSlice/AccessLimitApi';
import { getAuthState } from '../../store/AuthSlice';
import { getTrials } from '../../store/V2/TrialsSlice';
import { useAppSelector } from '../../hooks';
import {
  checkDiscountEligibility,
  checkUserPermission,
  getAppName,
  getUrlConvertedString,
  isMobileScreen,
  setClassToLocalStorage,
} from '../../helper';
import { TP_COLORS } from '../../constants';

import HomeHeader from './components/HomeHeader';
import BasicLoader from '../../components/Loaders/BasicLoader';
import UpcomingClassBanner from './components/UpcomingClassBanner';
import {
  getScheduleState,
  getUserSchedulesAsUser,
  IScheduleShortEvent,
} from '../../store/V2/ScheduleSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../store/V2/RolesSlice';
import DiscountBanner from './components/DiscountBanner';
import Footer from '../V2/Portal/components/Footer';
import FeaturesSection from './components/FeaturesSection';
import FeatureDescriptions from './components/FeatureDescriptions';
import CurriculumSection from './components/CurriculumSection';
import CurriculumModal from './modals/CurriculumModal';
import ReviewsSection from './components/ReviewsSection';
import AnimatedHeading from './components/AnimatedHeading';
import FullScreenLoader from '../../components/Loaders/FullScreenloader';
import PearsonBanner from './components/PearsonBanner';

interface IState {
  selectedCurriculum: ICurriculumForHome | undefined;
}

// DATA
const initialState: IState = {
  selectedCurriculum: undefined,
};

const scrollText = keyframes`
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const sectionTwoTypoProps: TypographyProps['sx'] = {
  component: 'h1',
  fontSize: '52px',
  fontWeight: 800,
  color: '#1E293B',
  textAlign: 'center',
  letterSpacing: 0,
  fontFamily: 'inherit',
};

const sectionTwoShadingTypoProps: TypographyProps['sx'] = {
  component: 'h1',
  color: '#1E293B',
  textAlign: 'center',
  letterSpacing: 0,
  fontFamily: 'inherit',
  fontSize: '52px',
  fontWeight: 800,
  background:
    'linear-gradient(to right, rgba(30, 41, 59, 0.6) 0%, rgba(82, 112, 161, 0.4) 60%, rgba(30, 41, 59, 0.2) 85%, rgba(30, 41, 59, 0.1) 95%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'inline-block',
  width: 'fit-content',
};

const paragraphTypoProps: TypographyProps['sx'] = {
  component: 'p',
  fontSize: '20px',
  color: '#1E293B',
  textAlign: 'center',
  letterSpacing: 0,
  marginTop: '25px',
};

const greetingSubTypoProps: TypographyProps['sx'] = {
  component: 'p',
  variant: 'subtitle1',
  color: '#0F172A',
  fontWeight: 600,
  fontSize: '14px',
  marginLeft: '-15px',
  marginTop: '35px',
};

const aiTypoSubProps: TypographyProps = {
  component: 'span',
  fontSize: '20px',
  fontWeight: 400,
  sx: {
    background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
};

const italicTypoProps: TypographyProps['sx'] = {
  component: 'p',
  fontFamily: 'cursive',
  color: '#0F172A',
  fontSize: '22px',
  textAlign: { xs: 'center', sm: 'center', md: 'left' },
  marginTop: '15px',
};

const sectionOne: BoxProps['sx'] = {
  background: `url(${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/graphic1.png), url(${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/graphic2.png)`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
};

const sectionTwo: BoxProps['sx'] = {
  background: `linear-gradient(103.87deg, rgba(236, 229, 219, 0.3) 7.37%, rgba(198, 195, 242, 0.3) 100%)`,
  paddingTop: '80px',
  paddingBottom: '25px',
};

const bookBtnProps: ButtonProps = {
  variant: 'contained',
  sx: {
    fontSize: '15px',
    fontWeight: 700,
    borderRadius: '8px',
    padding: '8px 20px',
  },
};

const fadingLine: BoxProps['sx'] = {
  mx: 'auto',
  borderTop: '2px solid',
  borderImageSource:
    'linear-gradient(to right, rgba(0, 0, 0, 0), #94A3B8, rgba(0, 0, 0, 0))',
  borderImageSlice: 1,
  width: '30%',
  marginBottom: '60px',
};

const awardingBox: BoxProps['sx'] = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  gap: { xs: 2, md: 4 },
  mt: 4,
};

const imageStyles = { width: '24px', height: '24px', marginRight: '7px' };

const featuredList = [
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/logo-moneyfm.png`,
    alt: 'logo-money-fm',
  },
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/logo-bt.png`,
    alt: 'logo-bt',
  },
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/logo-dna.png`,
    alt: 'logo-dna',
  },
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/logo-indvstrvs.png`,
    alt: 'logo-indvstrvs',
  },
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/logo-cna.png`,
    alt: 'logo-cna',
  },
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/logo-m5.png`,
    alt: 'logo-5',
  },
];

const filterOutExpiredUpcomingClasses = (item: IScheduleShortEvent) => {
  return (
    moment(item.start) > moment() ||
    (moment() >= moment(item.start) && moment() < moment(item.end))
  );
};

const sortUpcomingClasses = (a: IScheduleShortEvent, b: IScheduleShortEvent) =>
  new Date(a.start).getTime() - new Date(b.start).getTime();

const Home: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = isMobileScreen();

  // Store
  const curriculumState = useAppSelector(getCurriculumState);
  const bookmarkState = useAppSelector(getBookmarkState);
  const authState = useAppSelector(getAuthState);
  const metaTagState = useAppSelector(getMetaTagState);
  const scheduleState = useAppSelector(getScheduleState);

  const authUser = authState.user;
  const { curriculumsForHomePage, status: curriculumStatus } = curriculumState;
  const { bookmark } = bookmarkState;

  // State
  const [state, setState] = useState<IState>(initialState);
  const [isJoinCalssBannerOpen, setJoinCalssBannerModal] = useState(false);
  const [isCurriculumModalOpen, setIsCurriculumModalOpen] = useState(false);
  const [isDiagnosticFlow, setIsDiagnosticFlow] = useState(true);

  const queryStringParams = new URLSearchParams(location.search);
  const urlCurriculum = queryStringParams.get('curriculum') || '';
  const urlSubject = queryStringParams.get('subject') || '';
  const urlGrade = queryStringParams.get('grade') || '';
  const urlTutor = queryStringParams.get('host') || '';
  const urlStudent = queryStringParams.get('participant') || '';
  const urlStart = queryStringParams.get('start') || '';
  const urlEnd = queryStringParams.get('end') || '';
  const urlMeeting = queryStringParams.get('meeting') || '';

  const userScheduleData = scheduleState?.userScheduleData?.list || [];

  const upcomingClass = userScheduleData.length
    ? userScheduleData
        .filter(filterOutExpiredUpcomingClasses)
        .sort(sortUpcomingClasses)[0]
    : undefined;

  const isDiscountEligible = checkDiscountEligibility();

  const discountBannerPermission = checkUserPermission(authUser, {
    headerContents: [
      USER_PERMISSION_INDIVIDUALS.HEADER_CONTENTS_DISCOUNT_BANNER,
    ],
  });

  const dashboardPermission = checkUserPermission(authUser, {
    headerContents: [USER_PERMISSION_INDIVIDUALS.HEADER_CONTENTS_DASHBOARD],
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (curriculumsForHomePage.length === 0) {
      dispatch(getCurriculumsForHomePage());
    }

    if (urlTutor && urlStudent && urlStart && urlEnd && urlMeeting) {
      const classData = {
        tutorId: urlTutor,
        studentId: urlStudent,
        subject: urlSubject,
      };
      setClassToLocalStorage({
        ...classData,
        start: urlStart,
        end: urlEnd,
        meetingId: urlMeeting,
      });
      dispatch(getBookmark(classData));
    }

    if (authUser) {
      dispatch(getTrials());

      if (authUser.role.name === 'Student' || authUser.role.name === 'Guest') {
        dispatch(getUsageByUserId(authUser._id));
      }

      if (authUser && authUser.role.name !== 'Guest') {
        dispatch(
          getUserSchedulesAsUser({
            userId: authUser._id,
            startDate: moment().subtract(3, 'hour').toISOString(),
            endDate: moment().add(5, 'hour').toISOString(),
          })
        );
        setJoinCalssBannerModal(true);
      }
    }

    // To remove the last '/' mark from URL
    if (location.pathname.slice(-1) === '/') {
      navigate(location.pathname.slice(0, -1));
    }
  }, []);

  useEffect(() => {
    if (curriculumsForHomePage.length) {
      const curriculum = (params.curriculum || 'cambridge_igcse').trim();
      const _index = curriculumsForHomePage.findIndex(
        (curriculumForHomePage) =>
          getUrlConvertedString(curriculumForHomePage.name) ===
          curriculum.trim()
      );

      let selectedCurriculum: IState['selectedCurriculum'];

      if (_index >= 0) {
        selectedCurriculum = curriculumsForHomePage[_index];
      }

      if (urlCurriculum && urlSubject && urlGrade) {
        const selectedCurriculumItem = curriculumsForHomePage.find(
          (item) => item.name === urlCurriculum
        );

        if (
          selectedCurriculumItem &&
          selectedCurriculumItem.subjects &&
          selectedCurriculumItem.subjects.length
        ) {
          const selectedSubjectItem = selectedCurriculumItem.subjects.find(
            (item) => item.name === urlSubject && item.grade === urlGrade
          );

          if (selectedSubjectItem) {
            const urlConvertedSubject = getUrlConvertedString(
              selectedSubjectItem.name
            );

            const urlConvertedCurriculum = getUrlConvertedString(urlCurriculum);
            const urlConvertedTopic = getUrlConvertedString(
              selectedSubjectItem.subjectLevel
            );
            let urlConvertedSubTopic = getUrlConvertedString(
              selectedSubjectItem.subTopic && selectedSubjectItem.subTopic.name
                ? selectedSubjectItem.subTopic.name
                : ''
            );

            let subTopicId =
              selectedSubjectItem.subTopic && selectedSubjectItem.subTopic._id
                ? selectedSubjectItem.subTopic._id
                : '';

            if (bookmark.subTopicId && bookmark.subTopicName) {
              urlConvertedSubTopic = getUrlConvertedString(
                bookmark.subTopicName
              );
              subTopicId = bookmark.subTopicId;
            }

            navigate(
              `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicId}`
            );
          }
        }
      } else if (dashboardPermission) {
        navigate(`${process.env.PUBLIC_URL}/portal/dashboard`);
      }

      setState((prevState) => ({
        ...prevState,
        selectedCurriculum,
      }));
    }
  }, [curriculumsForHomePage, bookmark, params.curriculum]);

  useEffect(() => {
    if (params.curriculum) {
      const scrollToCurriculums = () => {
        const assessmentSection = document.getElementById('curriculum-section');
        if (assessmentSection) {
          const offset = 120;
          const elementPosition =
            assessmentSection.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      };

      const timeoutId = setTimeout(scrollToCurriculums, 1000);
      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [params.curriculum]);

  const handleCurriculumCloseModal = () => {
    setIsCurriculumModalOpen(false);
  };

  let metaTitle = `IGCSE And GCSE - Maths And Science Questions with Answers By Topic | ${getAppName()}`;

  let metaDesc =
    'Science and Maths questions with answers organized by topic. Our questions and answers are designed to help students prepare for their exams. Give them a try today!';

  if (state.selectedCurriculum) {
    metaTitle = `${
      state.selectedCurriculum.name || ''
    } - Maths And Science Questions with Answers By Topic | ${getAppName()}`;

    metaDesc = `${
      state.selectedCurriculum.name || ''
    } maths and science questions with answers organized by topic. Our questions and answers are designed to help students prepare for their exams. Give them a try today!`;
  }

  if (curriculumStatus === 'loading') {
    return <FullScreenLoader />;
  }

  return (
    <Box>
      {!metaTagState.metaTags ||
        (!metaTagState.metaTags[location.pathname.replace(/[/]$/, '')] && (
          <Helmet>
            <title>{metaTitle}</title>
            <meta name="description" content={metaDesc} />
          </Helmet>
        ))}

      <HomeHeader />

      {upcomingClass && (
        <UpcomingClassBanner
          open={isJoinCalssBannerOpen}
          onClose={() => setJoinCalssBannerModal(false)}
          classData={upcomingClass}
        />
      )}

      {((authUser && discountBannerPermission) || !authUser) &&
      isDiscountEligible &&
      !upcomingClass ? (
        <DiscountBanner />
      ) : null}

      <div id="home-container" style={{ minHeight: '80vh' }}>
        <div style={{ height: '60px' }} />
        <Box component="section" sx={sectionOne}>
          <Container maxWidth="lg">
            <div style={{ height: '120px' }} />
            <AnimatedHeading />
            <Typography {...paragraphTypoProps}>
              Trusted by thousands of students and parents, our{' '}
              <Typography {...aiTypoSubProps}>AI-driven</Typography> system
              helps you <br /> study smarter and achieve higher grades—no
              guesswork.
            </Typography>
            <Stack
              component="section"
              direction="row"
              alignItems="flex-start"
              justifyContent="center"
              width="100%"
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/avatar1.png`}
                alt="avatar"
                style={{ width: '170px', height: '120px', marginLeft: '-30px' }}
              />
              <Typography id="top-of-search" {...greetingSubTypoProps}>
                200+ Happy Students
              </Typography>
            </Stack>
            <Box position="relative" width="fit-content" mx="auto">
              {!isMobile && (
                <img
                  src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/arrow.png`}
                  alt="rotated-arrow"
                  style={{
                    position: 'absolute',
                    top: '20px',
                    left: '-40px',
                    width: '40px',
                  }}
                />
              )}
              <Stack
                component="section"
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
                gap="15px"
              >
                <Button
                  sx={{
                    ...bookBtnProps.sx,
                    backgroundColor: '#1E293B',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1E293B',
                      color: 'white',
                    },
                  }}
                  onClick={() => {
                    setIsDiagnosticFlow(true);
                    setIsCurriculumModalOpen(true);
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/quiz.png`}
                    alt="quiz"
                    style={imageStyles}
                  />
                  Start with Diagnostic
                </Button>
                <Button
                  sx={{
                    ...bookBtnProps.sx,
                    backgroundColor: 'white',
                    color: '#1E293B',
                    border: '1px solid #1E293B',
                  }}
                  onClick={() => {
                    setIsDiagnosticFlow(false);
                    setIsCurriculumModalOpen(true);
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/book.png`}
                    alt="book"
                    style={imageStyles}
                  />
                  Start Learning
                </Button>
              </Stack>
              <Box mt={2} {...italicTypoProps}>
                Check your strength and weakness
              </Box>
            </Box>
          </Container>
          <Box
            sx={{
              overflow: 'hidden',
              width: '100%',
              height: '185px',
              position: 'relative',
              whiteSpace: 'nowrap',
              mt: '-80px',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                animation: `${scrollText} 120s linear infinite`,
              }}
            >
              {[...Array(2)].map((_, index) => (
                <svg
                  key={index}
                  width="2200"
                  height="200"
                  viewBox="0 0 2200 200"
                  fill="none"
                  style={{ marginRight: '30px' }}
                >
                  <text
                    x="0"
                    y="180"
                    fontSize="180"
                    fontWeight="900"
                    fill="none"
                    stroke="#919EAB3D"
                    strokeWidth="2"
                    strokeDasharray="2, 4"
                    fontFamily="Poppins, sans-serif"
                  >
                    LEARNING-PLATFORM
                  </text>
                </svg>
              ))}
            </Box>
          </Box>
          <PearsonBanner />
        </Box>

        <Box component="section" sx={sectionTwo}>
          <Container maxWidth="xl">
            <Typography {...sectionTwoTypoProps}>
              How it b
              <img
                src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/rocket.png`}
                alt="rocket"
                height={68}
                width={73}
                style={{
                  verticalAlign: 'middle',
                  marginTop: '5px',
                }}
              />
              st your{' '}
              <Typography sx={sectionTwoShadingTypoProps}>learning</Typography>
            </Typography>
            <FeaturesSection />
          </Container>
        </Box>

        <FeatureDescriptions />

        <Container id="curriculum-section" component="section" maxWidth="lg">
          <Typography sx={{ ...sectionTwoTypoProps, marginTop: '60px' }}>
            Choose Your{' '}
            <Typography sx={sectionTwoShadingTypoProps}>Curriculum</Typography>
          </Typography>
          {bookmarkState.status === 'loading' ? (
            <BasicLoader
              containerProps={{ minHeight: '362px', alignItems: 'center' }}
            />
          ) : (
            <CurriculumSection
              curriculumsForHomePage={curriculumsForHomePage}
              selectedCurriculum={state.selectedCurriculum}
            />
          )}
        </Container>

        <Box
          component="section"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            paddingTop: '20px',
            paddingBottom: '10%',
          }}
        >
          {/* Background Shades */}
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/left.png`}
            alt="left-shade"
            sx={{
              position: 'absolute',
              top: '50%',
              left: 0,
              transform: 'translateY(-50%)',
              zIndex: 1,
              width: '600px',
            }}
          />
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/right.png`}
            alt="right-shade"
            sx={{
              position: 'absolute',
              top: '50%',
              right: 0,
              transform: 'translateY(-50%)',
              zIndex: 1,
              width: '600px',
            }}
          />
          <Container component="section" maxWidth="lg">
            <Typography sx={{ ...sectionTwoTypoProps, mt: '60px', mb: '10px' }}>
              What Our{' '}
              <Typography sx={sectionTwoShadingTypoProps}>
                Students Say
              </Typography>
            </Typography>
            <Typography variant="h5" textAlign="center" color="text.primary">
              Real Success, Real Stories!
            </Typography>
            <ReviewsSection />
          </Container>

          <Divider
            flexItem
            sx={{
              borderStyle: 'dashed',
              borderColor: TP_COLORS.border.color2,
              mt: '50px',
            }}
          />

          {/* Featured Section */}
          <Container maxWidth="lg" sx={{ pt: '30px' }}>
            <Typography sx={{ ...sectionTwoTypoProps, marginTop: '60px' }}>
              As Featured{' '}
              <Typography sx={sectionTwoShadingTypoProps} component="span">
                On
              </Typography>
            </Typography>
            <Box sx={fadingLine} />
            <Grid container mb="30px">
              {featuredList.map((element, index) => (
                <Grid
                  key={index}
                  item
                  xs={6}
                  sm={4}
                  md={2}
                  lg={2}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={element.src}
                    alt={element.alt}
                    style={{
                      width:
                        element.alt === 'logo-indvstrvs' ? '150px' : '100px',
                      height:
                        element.alt === 'logo-indvstrvs' ? '31px' : '65px',
                      margin: isMobile ? '20px' : '10px',
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>

          {/* Awards Section */}
          <Container maxWidth="lg">
            <Typography sx={{ ...sectionTwoTypoProps, marginTop: '100px' }}>
              Awards &{' '}
              <Typography sx={sectionTwoShadingTypoProps} component="span">
                Accreditation
              </Typography>
            </Typography>
            <Box sx={fadingLine} />
            <Box sx={awardingBox}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/award-apac.png`}
                alt="apac"
                sx={{
                  width: { xs: '210px', md: '440px' },
                  height: '55px',
                }}
              />

              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/award-sl.png`}
                alt="sl"
                sx={{
                  width: '76px',
                  height: '76px',
                }}
              />

              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/award-venture.png`}
                alt="venture"
                sx={{
                  width: { xs: '180px', md: '236px' },
                  height: '64px',
                }}
              />
            </Box>
          </Container>
        </Box>
      </div>

      <Footer />

      {/* Modal */}
      <CurriculumModal
        open={isCurriculumModalOpen}
        onClose={handleCurriculumCloseModal}
        curriculumsForHomePage={curriculumsForHomePage}
        isDiagnosticFlow={isDiagnosticFlow}
      />
    </Box>
  );
};

export default Home;
